import { Injectable, inject } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { AuthService } from '../auth/auth.service';
import { PATIENT_SIGNALR_URL } from '../../constants/api-url';
import { Auth } from '../../store/auth/auth.action';
import { Store } from '@ngxs/store';
import { ToastrHelperService } from './toast-helper.service';
import { TranslationService } from '@waseel-asp/tpa-waseel-lib-staging';
import { AuthState } from '../../store/auth/auth.state';
import { NotificationService } from './common-notification.service';
import { INotification, IPatientNotificationMessage, IPayerNotificationMessage, IProviderNotificationMessage, NotificationType } from '../../models/notification.model';

@Injectable({
  providedIn: 'root'
})
export class SignalrService {
  private authService = inject(AuthService);
  private notificationService = inject(NotificationService);
  private store = inject(Store);
  private toastrHelperService = inject(ToastrHelperService);
  private translation = inject(TranslationService);

  private hubConnection!: signalR.HubConnection;

  public startConnection(): void {
    if (this.hubConnection && this.hubConnection.state === signalR.HubConnectionState.Connected)
      return;

    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(PATIENT_SIGNALR_URL, { accessTokenFactory: () => this.authService.getToken() || "" })
      .withAutomaticReconnect([0, 2000, 5000, 10000, null])
      .configureLogging(signalR.LogLevel.Information)
      .build();

    this.hubConnection.start().then(() => {
      this.registerOnServerEvents();
    }).catch(() => {
      setTimeout(() => this.startConnection(), 5000);
    });

    this.hubConnection.onreconnected(connectionId => console.info("SignalR reconnected:", connectionId));

    this.hubConnection.onclose(error => {
      if (error)
        console.error("SignalR closed due to error:", error);
      else
        console.info("SignalR closed gracefully");
    });
  }

  // Register all server-side events
  private registerOnServerEvents(): void {
    if (!this.hubConnection) return;

    this.hubConnection.on("ReceiveNotification", (message: INotification) => {
      if ([NotificationType.PatientNotification, NotificationType.ProviderNotification, NotificationType.PayerNotification].includes(message.notificationType)) {
        this.notificationService.addNotification(message.notification as (IPatientNotificationMessage | IProviderNotificationMessage | IPayerNotificationMessage));
      } else if (message.notificationType === NotificationType.LogOut) {
        this.toastrHelperService.showWarningMessage(this.translation.instant("COMMON.ACCOUNT_UPDATED"));
        this.store.dispatch(new Auth.Logout(this.store.selectSnapshot(AuthState.isPatientUser)));
      }
    });
  }

  // Send message to server
  public async sendMessage(message: string): Promise<void> {
    if (!this.hubConnection || this.hubConnection.state !== signalR.HubConnectionState.Connected) {
      console.warn("SignalR not connected, cannot send message.");
      return;
    }
    try {
      await this.hubConnection.invoke("SendNotificationToAll", message);
    } catch (err) {
      console.error("Error while sending message:", err);
    }
  }

  // Stop connection (logout / app destroy)
  public async stopConnection(): Promise<void> {
    if (this.hubConnection) {
      await this.hubConnection.stop();
      this.hubConnection = undefined;
    }
  }
}